<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { CloseIcon } from "../icons";
import {
  ratingRange,
  calculateField,
  canonicalValue,
  formatDecimal,
  type Field,
} from "@yawhub/core";
import { api, errorText, ApiError } from "../api";
import RatingInput from "./RatingInput.vue";
import ImageInput from "./ImageInput.vue";
const props = defineProps<{
    dataset: string;
    fields: Field[];
    record?: any;
    formId?: string;
  }>(),
  emit = defineEmits(["close", "saved"]);
const dialog = ref<HTMLDialogElement>(),
  data = ref<Record<string, any>>({}),
  busy = ref(false),
  error = ref(""),
  issues = ref<any[]>([]);
const imageUploads = ref<Record<string, boolean>>({});
const uploading = computed(() =>
  Object.values(imageUploads.value).some(Boolean),
);
const visible = computed(() =>
  props.fields.filter((f) => f.active && !f.hidden && !f.inputHidden),
);
function nowValue(f: Field) {
  const now = new Date();
  return f.type === "datetime"
    ? now.toISOString()
    : new Intl.DateTimeFormat("sv-SE", {
        timeZone: "Europe/Copenhagen",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(now);
}
function calculationPreview(field: Field) {
  const values: Record<string, unknown> = {};
  try {
    for (const id of field.calculation!.fieldIds) {
      const source = props.fields.find((f) => f.id === id);
      if (!source || source.inputHidden || source.hidden)
        return "Beregnes ved gem";
      values[id] = canonicalValue(
        source,
        data.value[id] === "" ? null : data.value[id],
      );
    }
    return formatDecimal(calculateField(field, values));
  } catch {
    return "—";
  }
}
onMounted(() => {
  for (const f of props.fields)
    data.value[f.id] =
      props.record?.data?.[f.id] ??
      (!props.record && f.defaultNow && !f.inputHidden ? nowValue(f) : "");
  dialog.value?.showModal();
});
async function save() {
  if (uploading.value) return;
  busy.value = true;
  error.value = "";
  issues.value = [];
  try {
    const values: Record<string, unknown> = {};
    for (const f of props.fields.filter((f) => f.active)) {
      if (
        f.calculation ||
        f.inputHidden ||
        (f.identifier && f.identifier.mode !== "manual")
      )
        continue;
      let v = data.value[f.id];
      if (v === "" || v === undefined) v = null;
      if (f.type === "json" && typeof v === "string" && v) v = JSON.parse(v);
      if (f.type === "boolean" && typeof v === "string" && v !== "")
        v = v === "true";
      if (
        f.type === "datetime" &&
        typeof v === "string" &&
        v &&
        !/Z|[+-]\d\d:\d\d$/.test(v)
      )
        v = new Date(v).toISOString();
      values[f.id] = v;
    }
    await api(
      props.formId
        ? "/api/forms/" + props.formId + "/records"
        : "/api/datasets/" +
            props.dataset +
            "/records" +
            (props.record ? "/" + props.record.id : ""),
      {
        method: props.record ? "PATCH" : "POST",
        body: { data: values },
        revision: props.record?.revision,
      },
    );
    emit("saved");
    emit("close");
  } catch (e) {
    error.value = errorText(e);
    if (e instanceof ApiError && Array.isArray(e.details))
      issues.value = e.details;
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <dialog ref="dialog" class="record-dialog" @cancel.prevent="emit('close')">
    <form @submit.prevent="save">
      <div class="dialog-heading">
        <div>
          <h2>{{ record ? "Redigér række" : "Tilføj en række" }}</h2>
          <p class="muted">
            {{
              record
                ? "Ændringer gemmes med revisionskontrol."
                : "Felterne følger dit aktive schema."
            }}
          </p>
        </div>
        <button
          type="button"
          class="icon-button"
          aria-label="Luk formular"
          @click="emit('close')"
        >
          <CloseIcon />
        </button>
      </div>
      <div class="form-stack">
        <div v-for="f in visible" :key="f.id" class="record-field">
          <label :for="'entry-' + f.id"
            >{{ f.label
            }}<span v-if="f.unit" class="muted"> · {{ f.unit }}</span
            ><span v-if="!f.nullable">&nbsp;*</span></label
          >
          <template v-if="f.calculation">
            <output :id="'entry-' + f.id" class="generated-value">{{
              calculationPreview(f)
            }}</output>
            <span class="muted small"
              >Gennemsnit · beregnes automatisk ved gem.</span
            >
          </template>
          <p
            v-else-if="f.identifier && f.identifier.mode !== 'manual'"
            class="generated-value"
          >
            {{ data[f.id] || "Tildeles, når rækken gemmes" }}
          </p>
          <ImageInput
            v-else-if="f.type === 'image'"
            :dataset="dataset"
            :field="f.id"
            :label="f.label"
            :form="formId"
            :disabled="busy"
            v-model="data[f.id]"
            @busy="imageUploads[f.id] = $event"
          />
          <select
            v-else-if="f.type === 'boolean'"
            :id="'entry-' + f.id"
            v-model="data[f.id]"
          >
            <option value="">Ikke angivet</option>
            <option :value="true">Ja</option>
            <option :value="false">Nej</option></select
          ><RatingInput
            v-else-if="f.type === 'rating'"
            :id="'entry-' + f.id"
            :field="f"
            v-model="data[f.id]"
          /><select
            v-else-if="f.type === 'enum'"
            :id="'entry-' + f.id"
            v-model="data[f.id]"
          >
            <option value="">Vælg…</option>
            <option v-for="opt in f.options" :key="opt">
              {{ opt }}
            </option></select
          ><textarea
            v-else-if="f.type === 'json'"
            :id="'entry-' + f.id"
            :value="
              typeof data[f.id] === 'object'
                ? JSON.stringify(data[f.id], null, 2)
                : data[f.id]
            "
            @input="data[f.id] = ($event.target as HTMLTextAreaElement).value"
            rows="4"
          /><input
            v-else
            :id="'entry-' + f.id"
            v-model="data[f.id]"
            :required="!f.nullable"
            :type="f.type === 'date' ? 'date' : 'text'"
            :inputmode="
              ['integer', 'decimal'].includes(f.type) ? 'decimal' : 'text'
            "
            :placeholder="
              f.type === 'datetime' ? '2026-09-29T10:00:00+02:00' : ''
            "
          /><button
            v-if="f.stampNow && ['date', 'datetime'].includes(f.type)"
            type="button"
            class="text-button stamp-now"
            @click="data[f.id] = nowValue(f)"
          >
            Sæt til nu</button
          ><span
            v-for="issue in issues.filter((i) => i.fieldId === f.id)"
            :key="issue.message"
            class="field-error"
            >{{ issue.message }}</span
          >
        </div>
      </div>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <div class="form-actions">
        <button type="button" class="button secondary" @click="emit('close')">
          Annullér</button
        ><button class="button primary" :disabled="busy || uploading">
          {{ busy ? "Gemmer…" : "Gem række" }}
        </button>
      </div>
    </form>
  </dialog>
</template>
<style scoped lang="scss" src="../styles/components/RecordForm.scss"></style>
